/**
 * `/app/documents?path=<relative path>` — the landing page every agent SMS
 * points at when it texts you one of your own documents.
 *
 * **Why the link is minted here and not put in the text.** A presigned S3 URL
 * measures ~1.5 KB — an SMS body is capped at 600 chars, so it simply does not
 * fit; it expires inside the hour, so a text read the next morning is dead; and
 * it is a bearer credential, which SMS carries in cleartext. This page keeps the
 * text short (~110 chars) and the credential off the wire: the link is signed
 * on demand, sub-scoped server-side by the `/user-files` Lambda, only after the
 * visitor proves who they are.
 *
 * **Why it lives under `/app`.** `postAuthRedirect` only round-trips paths
 * matching `^/(agent|app)(\/|$)`. A text is usually opened on a phone that
 * isn't signed in, so a route anywhere else would bounce through sign-in and
 * drop the document the user asked for. Same reasoning as `/app/widgets/add`.
 *
 * Auth-gated, and the API is sub-scoped, so a forwarded text can never open
 * someone else's document — a foreign path just fails to mint.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getDownloadUrl, type DownloadLink } from '../../agents/userFilesApi.ts';
import { isImageName } from './documentKind.ts';
import './OpenDocument.css';

type Phase = 'loading' | 'ready' | 'missing' | 'error';

/**
 * Reject anything that isn't a plain relative document path before spending a
 * request on it. The `/user-files` Lambda is authoritative (it derives the S3
 * key from the trusted `sub`); this is the fast fail that keeps an obviously
 * malformed deep link from looking like a server problem.
 */
function isRelativeDocumentPath(path: string): boolean {
  if (path.length === 0 || path.length > 1024) {
    return false;
  }
  if (path.startsWith('/') || path.includes('\\') || path.includes('://')) {
    return false;
  }
  return !path.split('/').includes('..');
}

/**
 * The ready state — the minted link, offered by file name.
 *
 * Exported so the DEV mobile-layout harness can mount the REAL markup with a
 * hostile filename. Agents write long, descriptive slugs
 * (`comprehensive-defense-drone-breakout-analysis.md`), and a long unbroken
 * token in a bordered CTA is precisely the class of defect the 390×844 gate
 * exists to catch — but the ready state needs a successful mint, which an
 * offline harness cannot produce through the page itself.
 */
export function DocumentReady({ link }: { readonly link: DownloadLink }) {
  // An image is something to LOOK at. Forcing `download` on content S3 already
  // serves inline turns "here's your picture" into a file-save prompt — the
  // agent texted it to be seen.
  if (isImageName(link.name)) {
    return (
      <>
        <p>Your image:</p>
        <a
          className="om-doc__image"
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={link.name}
        >
          <img className="om-doc__img" src={link.url} alt={link.name} />
        </a>
        <p className="om-doc__muted">
          {link.name} — tap to open full size. This link is signed just for you and is good for
          a short while.
        </p>
        <Link className="om-doc__link" to="/app">
          Back to your console
        </Link>
      </>
    );
  }

  return (
    <>
      <p>Ready to download:</p>
      <a className="om-doc__cta" href={link.url} rel="noopener noreferrer" download={link.name}>
        {link.name}
      </a>
      <p className="om-doc__muted">
        This download link is signed just for you and is good for a short while. If it stops
        working, reopen this page for a fresh one.
      </p>
      <Link className="om-doc__link" to="/app">
        Back to your console
      </Link>
    </>
  );
}

export default function OpenDocument() {
  const [params] = useSearchParams();
  // useSearchParams has already percent-decoded the value, so `path` arrives
  // with its slashes intact — do NOT decode again.
  const path = params.get('path') ?? '';
  const valid = isRelativeDocumentPath(path);

  const [phase, setPhase] = useState<Phase>(valid ? 'loading' : 'missing');
  const [link, setLink] = useState<DownloadLink | null>(null);
  const [error, setError] = useState<string>('');

  const mint = useCallback(async (): Promise<void> => {
    setPhase('loading');
    try {
      setLink(await getDownloadUrl(path));
      setPhase('ready');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open that document.');
      setPhase('error');
    }
  }, [path]);

  useEffect(() => {
    if (!valid) {
      setPhase('missing');
      return;
    }
    void mint();
  }, [valid, mint]);

  return (
    <main className="om-doc">
      <h1 className="om-doc__title">Your document</h1>

      {phase === 'loading' ? <p className="om-doc__muted">Preparing your file…</p> : null}

      {phase === 'missing' ? (
        <>
          <p>This link didn’t name a document to open.</p>
          <p className="om-doc__muted">
            No document was requested. Open your console to browse everything your agents have
            written for you.
          </p>
          <Link className="om-doc__link" to="/app">
            Back to your console
          </Link>
        </>
      ) : null}

      {phase === 'ready' && link !== null ? <DocumentReady link={link} /> : null}

      {phase === 'error' ? (
        <>
          <p className="om-doc__error">{error}</p>
          <p className="om-doc__muted">
            The document may have been renamed or deleted, or it may belong to a different
            account.
          </p>
          <button
            type="button"
            className="om-doc__retry"
            onClick={() => {
              void mint();
            }}
          >
            Try again
          </button>
          <Link className="om-doc__link" to="/app">
            Back to your console
          </Link>
        </>
      ) : null}
    </main>
  );
}
